import { useCallback, useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { CaretLeft, CaretRight, Pause, Play } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;
const img = (path) => `${API}/api/public/showroom/photo/${path}`;

// Walk-up tablet slideshow — open link, no login, no prices.
export default function ShowroomDisplay() {
  const [feed, setFeed] = useState(null);
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);

  const load = useCallback(() => {
    fetch(`${API}/api/public/showroom/display`)
      .then((r) => r.json())
      .then((d) => setFeed(d))
      .catch(() => setFeed({ items: [], seconds: 12, shop: {} }));
  }, []);

  useEffect(() => {
    load();
    const refresh = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(refresh);
  }, [load]);

  const items = feed?.items || [];
  const step = (n) => setI((c) => (items.length ? (c + n + items.length) % items.length : 0));

  useEffect(() => {
    if (paused || items.length < 2) return;
    const t = setInterval(
      () => setI((c) => (c + 1) % items.length),
      (feed?.seconds || 12) * 1000,
    );
    return () => clearInterval(t);
  }, [paused, items.length, feed?.seconds]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
      if (e.key === " ") setPaused((p) => !p);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  if (!feed) return <div className="min-h-screen bg-black" />;

  const shop = feed.shop || {};
  const slab = items[i];

  if (!slab) {
    return (
      <div
        className="flex min-h-screen flex-col items-center justify-center gap-4 bg-black px-8 text-center"
        data-testid="display-empty"
      >
        <h1 className="text-3xl font-bold text-white">{shop.name}</h1>
        <p className="max-w-md text-sm text-white/50">
          No slabs are on the showroom screen yet. In the office, open Showroom
          and tick Screen on the slabs you want on display.
        </p>
      </div>
    );
  }

  const specs = [slab.material, slab.finish, slab.thickness].filter(Boolean);

  return (
    <div
      className="relative h-screen w-screen overflow-hidden bg-black text-white"
      data-testid="showroom-display"
      onClick={() => setPaused((p) => !p)}
    >
      {items.map((s, n) => (
        <img
          key={s.id}
          src={img(s.photo)}
          alt=""
          data-testid={n === i ? "display-photo" : undefined}
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-[1400ms] ${
            n === i ? "opacity-100" : "opacity-0"
          }`}
          style={n === i ? { animation: "display-pan 22s ease-out both" } : undefined}
        />
      ))}
      <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-black/70 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 h-[62%] bg-gradient-to-t from-black/92 via-black/45 to-transparent" />

      <div className="absolute inset-x-0 top-0 flex items-center justify-between px-8 py-7 sm:px-14">
        <span className="flex items-center gap-4">
          {shop.has_logo && (
            <img
              src={`${API}/api/public/logo`}
              alt=""
              className="h-9 w-auto brightness-0 invert"
            />
          )}
          <span className="text-sm font-bold uppercase tracking-[0.3em] text-white/80">
            {shop.name}
          </span>
        </span>
        <span
          className="text-xs uppercase tracking-[0.3em] text-white/50"
          data-testid="display-counter"
        >
          {i + 1} / {items.length}
        </span>
      </div>

      <div className="absolute inset-x-0 bottom-0 flex flex-wrap items-end justify-between gap-8 px-8 pb-10 sm:px-14 sm:pb-14">
        <div className="min-w-0">
          <span className="flex flex-wrap items-center gap-3">
            {slab.new_arrival && (
              <span className="rounded-full border border-emerald-300/50 bg-emerald-400/15 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.24em] text-emerald-200">
                New arrival
              </span>
            )}
            {slab.on_sale && (
              <span className="rounded-full border border-amber-300/50 bg-amber-400/15 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.24em] text-amber-200">
                On sale
              </span>
            )}
          </span>
          <h1
            className="mt-4 text-5xl font-black uppercase leading-[0.95] tracking-tight sm:text-7xl"
            data-testid="display-name"
          >
            {slab.color}
          </h1>
          <p className="mt-4 text-base uppercase tracking-[0.24em] text-white/70 sm:text-lg">
            {specs.join(" · ")}
          </p>
          <p className="font-mono-data mt-3 text-sm text-white/60 sm:text-base">
            {[slab.size, slab.sqft ? `${slab.sqft} ft²` : "", slab.code]
              .filter(Boolean)
              .join(" · ")}
          </p>
        </div>

        <div className="flex items-end gap-8">
          <div className="text-right">
            <p className="text-sm text-white/70">
              Like this one? Ask us about it.
            </p>
            {shop.phone && (
              <p className="font-mono-data mt-1 text-lg text-white">
                {shop.phone}
              </p>
            )}
            <p className="mt-3 text-[10px] uppercase tracking-[0.24em] text-white/40">
              Scan to see it online
            </p>
          </div>
          <span className=" bg-white p-2.5">
            <QRCodeSVG
              value={`${window.location.origin}/inventory/${slab.id}`}
              size={92}
              level="M"
            />
          </span>
        </div>
      </div>

      <div className="absolute bottom-1/2 left-4 right-4 flex justify-between opacity-0 transition-opacity duration-300 hover:opacity-100 sm:opacity-40">
        <button
          type="button"
          data-testid="display-prev"
          onClick={(e) => {
            e.stopPropagation();
            step(-1);
          }}
          className="flex h-14 w-14 items-center justify-center rounded-full border border-white/30 bg-black/40 backdrop-blur"
        >
          <CaretLeft size={20} weight="bold" />
        </button>
        <button
          type="button"
          data-testid="display-next"
          onClick={(e) => {
            e.stopPropagation();
            step(1);
          }}
          className="flex h-14 w-14 items-center justify-center rounded-full border border-white/30 bg-black/40 backdrop-blur"
        >
          <CaretRight size={20} weight="bold" />
        </button>
      </div>

      <span
        className="absolute right-8 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/25 bg-black/40 text-white/70 backdrop-blur sm:hidden"
        data-testid="display-state"
      >
        {paused ? <Play size={15} weight="fill" /> : <Pause size={15} weight="fill" />}
      </span>
    </div>
  );
}
